import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useModifyPasswordMutation } from '@features/user/hook';
import { useAlert } from '@shared/hooks';
import validatePassword from '@features/user/utils/validatePassword';
import { postLogout } from '@entities/auth/api/auth';
import useAuthStore from '@entities/auth/model/useAuthStore';
import { isMobileApp } from '@shared/utils/fcmToken';

/**
 * 비밀번호 변경 공통 비즈니스 로직 훅
 * - PC / Mobile 공통 사용
 */
function usePasswordChangeForm() {
  /* ======================
   * 상태
   ====================== */
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const { alertState, showAlert, closeAlert } = useAlert();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((state) => state.clearAuth);

  /* ======================
   * API
   ====================== */
  const modifyMutation = useModifyPasswordMutation({
    onSuccess: function () {
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      showAlert('비밀번호가 변경되었습니다. 다시 로그인해 주세요.', function onCloseLogout() {
        (async function doLogout() {
          try {
            await postLogout();
          } catch {
            // 서버 로그아웃 실패여도 클라이언트 정리 후 로그인으로 이동
          } finally {
            queryClient.clear();
            clearAuth();
            if (isMobileApp() && window.FlutterChannel?.postMessage) {
              try {
                window.FlutterChannel.postMessage('logout');
              } catch {
                // Flutter 채널 실패 시 무시
              }
            }
            navigate('/login');
          }
        })();
      });
    },
    onError: function (error) {
      showAlert(error?.friendlyMsg || '비밀번호 변경에 실패했습니다.');
    },
  });

  /* ======================
   * 핸들러
   ====================== */
  const handleInputChange = useCallback(function (field) {
    return function (e) {
      setFormData(function (prev) {
        return {
          ...prev,
          [field]: e.target.value,
        };
      });
    };
  }, []);

  const toggleShowPassword = useCallback(function (field) {
    return function () {
      setShowPassword(function (prev) {
        return {
          ...prev,
          [field]: !prev[field],
        };
      });
    };
  }, []);

  /* ======================
   * 검증
   ====================== */
  const isPasswordMismatch = useCallback(
    function () {
      if (!formData.newPassword || !formData.confirmPassword) {
        return false;
      }
      return formData.newPassword !== formData.confirmPassword;
    },
    [formData.newPassword, formData.confirmPassword],
  );

  /* ======================
   * 제출
   ====================== */
  const submit = useCallback(
    function () {
      if (!formData.currentPassword) {
        showAlert('기존 비밀번호를 입력해주세요.');
        return;
      }

      if (!formData.newPassword) {
        showAlert('새로운 비밀번호를 입력해주세요.');
        return;
      }

      if (!validatePassword(formData.newPassword)) {
        showAlert('비밀번호 생성 규칙을 확인해 주세요.');
        return;
      }

      if (!formData.confirmPassword) {
        showAlert('새로운 비밀번호 확인을 입력해주세요.');
        return;
      }

      if (formData.newPassword !== formData.confirmPassword) {
        showAlert('비밀번호가 일치하지 않습니다.');
        return;
      }

      // 새 비밀번호가 기존 비밀번호와 같은지 검증
      if (formData.currentPassword === formData.newPassword) {
        showAlert('새 비밀번호는 기존 비밀번호와 달라야 합니다.', function () {
          // Alert 닫힐 때 새 비밀번호와 새 비밀번호 확인 필드만 클리어
          setFormData(function (prev) {
            return {
              ...prev,
              newPassword: '',
              confirmPassword: '',
            };
          });
        });
        return;
      }

      modifyMutation.mutate({
        pw: formData.currentPassword,
        changePw: formData.newPassword,
      });
    },
    [formData, modifyMutation, showAlert],
  );

  return {
    formData,
    showPassword,
    alertState,
    isPending: modifyMutation.isPending,

    handleInputChange,
    toggleShowPassword,
    isPasswordMismatch,
    submit,
    closeAlert,
  };
}

export default usePasswordChangeForm;
